import { RouterLink } from 'vue-router';
import {
	createUserWithEmailAndPassword,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	updatePassword,
	updateProfile
} from 'firebase/auth';
import { firebaseAuth } from '../firebase';
import { http } from '../http';
import { base_api } from '../service';
import { Component, createCookie, getCookie } from '../helpers/helper';
import { router } from '../router';
import { state } from '../state';
import { store_api } from '../storeAPI';

function firebaseErrorMessage(err: any): string {
	const code = err?.code || '';
	switch (code) {
		case 'auth/invalid-email':
		case 'auth/user-not-found':
		case 'auth/wrong-password':
		case 'auth/invalid-credential':
			return 'Invalid email or password.';
		case 'auth/too-many-requests':
			return 'Too many failed attempts. Please try again later.';
		case 'auth/email-already-in-use':
			return 'An account with that email already exists.';
		case 'auth/weak-password':
			return 'Password must be at least 6 characters.';
		default:
			return err?.message || 'Login failed.';
	}
}

const DEFAULT_HOME_PAGE = '/dashboard';

class LoginState {
	email = null;
	name = null;
	password = null;
	confirmPassword = null;
	agree = false;
	pw_reset_code = null;
	showPasswordResetCode = false;
	mustReset = false;
	registering = false;
	loginError: string = null;

	async sendResetCode() {
		if (!this.email) {
			this.loginError = 'Please enter your email';
			return;
		}

		try {
			await sendPasswordResetEmail(firebaseAuth, this.email);
			this.loginError = null;
			this.showPasswordResetCode = true;
			this.password = null;
		} catch (err) {
			this.loginError = firebaseErrorMessage(err);
		}
	}

	async login() {
		if (this.showPasswordResetCode) {
			if (!this.pw_reset_code) {
				alert('You must enter the password reset code sent to your email.');
				return;
			}
			if (!this.confirmPasswords()) return;
		}

		this.loginError = null;
		try {
			const cred = await signInWithEmailAndPassword(firebaseAuth, this.email, this.password);
			const user = cred.user;
			const tokenResult = await user.getIdTokenResult();
			if (tokenResult.claims.mustResetPassword === true) {
				// Forced password change. Keep the user signed in (we need a fresh
				// credential to call updatePassword) and switch the form into
				// "set new password" mode.
				this.mustReset = true;
				this.password = null;
				this.confirmPassword = null;
				this.loginError = 'Please set a new password to continue.';
				return;
			}
			const session_id = tokenResult.token;
			state.setUser({
				success: true,
				email: user.email || this.email,
				name: user.displayName || undefined,
				session_id,
				id: user.uid,
				is_admin: tokenResult.claims.is_admin === true,
				is_dev: tokenResult.claims.is_dev === true
			});
			if (!getCookie('session_id')) {
				createCookie('session_id', session_id);
				createCookie('session_email', user.email || this.email);
			}
			router.push(DEFAULT_HOME_PAGE);
		} catch (err) {
			this.loginError = firebaseErrorMessage(err);
		}
	}

	async completeForcedReset() {
		if (!this.confirmPasswords()) return;
		const user = firebaseAuth.currentUser;
		if (!user) {
			this.loginError = 'Session expired, please log in again.';
			this.mustReset = false;
			return;
		}
		this.loginError = null;
		try {
			await updatePassword(user, this.password);
			await http.post(`${base_api}/account/complete_password_reset`, {});
			const tokenResult = await user.getIdTokenResult(true);
			const session_id = tokenResult.token;
			state.setUser({
				success: true,
				email: user.email || this.email,
				name: user.displayName || undefined,
				session_id,
				id: user.uid,
				is_admin: tokenResult.claims.is_admin === true,
				is_dev: tokenResult.claims.is_dev === true
			});
			if (!getCookie('session_id')) {
				createCookie('session_id', session_id);
				createCookie('session_email', user.email || this.email);
			}
			this.mustReset = false;
			router.push(DEFAULT_HOME_PAGE);
		} catch (err) {
			this.loginError = firebaseErrorMessage(err);
		}
	}

	confirmPasswords(): boolean {
		if (!this.password || this.password !== this.confirmPassword) {
			alert('Both password fields must match');
			return false;
		}
		return true;
	}

	async createAccount() {
		if (!this.name || !this.password || !this.email) {
			alert('Name, Email, and Password Required');
			return;
		}
		if (!this.agree) {
			alert("You must accept GatherAct's Terms of Service and Privacy Policy before you can create an account");
			return;
		}
		if (!this.confirmPasswords()) return;
		try {
			const cred = await createUserWithEmailAndPassword(firebaseAuth, this.email, this.password);
			if (this.name) {
				try { await updateProfile(cred.user, { displayName: this.name }); } catch { /* non-fatal */ }
			}
			const tokenResult = await cred.user.getIdTokenResult();
			const session_id = tokenResult.token;
			state.setUser({
				success: true,
				email: cred.user.email || this.email,
				name: this.name,
				session_id,
				id: cred.user.uid,
				is_admin: tokenResult.claims.is_admin === true,
				is_dev: tokenResult.claims.is_dev === true
			});
			router.push(DEFAULT_HOME_PAGE);
		} catch (err) {
			this.loginError = firebaseErrorMessage(err);
		}
	}
}

export const LoginPage = Component(() => {
	const l = Vue.reactive(new LoginState());

	return () => (
		<div class="store-page">
			<form
				id="login"
				onSubmit={e => {
					e.preventDefault();
					if (l.mustReset) l.completeForcedReset();
					else l.login();
					return false;
				}}
			>
				{l.mustReset && (
					<p>For security, please choose a new password to continue.</p>
				)}
				{l.showPasswordResetCode && (
					<p>You have been sent an email with a password reset code, Enter that code and your new password below.</p>
				)}
				<h3 class="welcome">Welcome to GatherAct!</h3>
				<h4 class="login">{l.mustReset ? 'Set New Password' : 'Login'}</h4>
				{!l.mustReset && (
					<div class="form-row">
						<label>Email</label>
						<input class="input" name="username" value={l.email} onChange={e => (l.email = e.target.value)} />
					</div>
				)}
				{l.registering && (
					<div class="form-row">
						<label>Name</label>
						<input class="input" value={l.name} onChange={e => (l.name = e.target.value)} />
					</div>
				)}
				{l.showPasswordResetCode && (
					<div class="form-row">
						<label>Password reset code</label>
						<input value={l.pw_reset_code} onChange={e => (l.pw_reset_code = e.target.value)} />
					</div>
				)}
				<div class="form-row">
					<label>{l.mustReset ? 'New Password' : 'Password'}</label>
					<input name="password" value={l.password} type="password" onChange={e => (l.password = e.target.value)} />
				</div>
				{(l.registering || l.showPasswordResetCode || l.mustReset) && (
					<div class="form-row">
						<label>Confirm Password</label>
						<input value={l.confirmPassword} onChange={e => (l.confirmPassword = e.target.value)} type="password" />
					</div>
				)}
				{l.registering && (
					<div class="form-row">
						<label style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
							<input
								type="checkbox"
								style={{ width: 'unset' }}
								checked={l.agree}
								onChange={e => (l.agree = e.target.value)}
							/>
							<span>
								I agree to GatherAct's
								<br />
								<a href="/terms_of_service" target="_blanks" class="inline">
									Terms of Service
								</a>
								&nbsp;and&nbsp;
								<a href="/privacy_policy" target="_blanks" class="inline">
									Privacy Policy
								</a>{' '}
							</span>
						</label>
					</div>
				)}
				{l.registering && (
					<div class="form-row horizontal gap20">
						<button type="button" onClick={e => l.createAccount()}>
							Create Account
						</button>
						<button type="button" onClick={e => (l.registering = false)}>
							Cancel
						</button>
					</div>
				)}
				{l.showPasswordResetCode && (
					<div class="form-row horizontal gap20">
						<button type="submit">Log in</button>
						<button type="button" onClick={e => (l.showPasswordResetCode = false)}>
							Cancel
						</button>
					</div>
				)}
				{!l.registering && !l.showPasswordResetCode && !l.mustReset && (
					<div class="form-row">
						<div style="display: flex">
							<button class="button button-accent" type="submit">
								Log in
							</button>
							<button class="button" type="button" onClick={e => l.sendResetCode()} style="margin-left: 12px">
								Forgot Password
							</button>
						</div>
						<div style="margin-top: 2rem">
							<button class="button" type="button" onClick={e => (l.registering = true)}>
								Register New Account
							</button>
						</div>
					</div>
				)}
				{l.mustReset && (
					<div class="form-row">
						<button class="button button-accent" type="submit">
							Set new password
						</button>
					</div>
				)}
				{l.loginError && <div class="form-row error">{l.loginError}</div>}
			</form>
		</div>
	);
});
